import React, { useEffect, useMemo, useState } from "react";
import { useTitle } from "../../context/TitleContext";
import PermissionButton from "../../components/PermissionButton";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import * as bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import {
  fetchProducts,
  deleteProduct,
  clearState,
  fetchAllCategories,
  fetchAllGrades,
  fetchAllSubjects,
  fetchAllSections,
} from "../../redux/Slices/ProductSlice";
import { getSettingsTeachers } from "../../redux/Slices/TeachersSlice";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function Products() {
  const { i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { settingsTeachers: teachers } = useSelector((state) => state.teachers);

  const {
    products,
    meta,
    isLoading,
    deleteLoading,
    error,
    success,
    categories,
    grades,
    subjects,
    sections,
  } = useSelector((state) => state.products);

  const [deletingProduct, setDeletingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInputs, setSearchInputs] = useState({
    category_id: "",
    grade_id: "",
    subject_id: "",
    section_id: "",
    teacher_id: "",
    status: "", // ✅ هنستخدم "1" / "0"
    min_price: "",
    max_price: "",
    has_discount: "", // "1" / "0"
    min_discount: "",
    max_discount: "",
    stock_status: "", // in/out/low
    date_from: "",
    date_to: "",
    sort_by: "", // newest/oldest/price/title/stock
    sort_dir: "", // asc/desc
  });

  const [fromFocused, setFromFocused] = useState(false);
  const [toFocused, setToFocused] = useState(false);

  // ✅ Applied filters
  const [appliedFilters, setAppliedFilters] = useState({});

  // ✅ memoized filters
  const memoFilters = useMemo(() => appliedFilters, [appliedFilters]);

  const isBookOrCodeCategory = (catNameEn) => {
    if (!catNameEn) return false;
    const nameEn = catNameEn.toLowerCase().trim();
    return nameEn.includes("books") || nameEn.includes("codes");
  };

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((product) => {
      return isBookOrCodeCategory(product.category_name_en);
    });
  }, [products]);

  // ✅ أول تحميل: title + lookups
  useEffect(() => {
    setTitle("المنتجات");
    dispatch(fetchAllCategories());
    dispatch(fetchAllGrades());
    dispatch(fetchAllSubjects());
    dispatch(fetchAllSections());
    dispatch(getSettingsTeachers());
  }, [setTitle, dispatch]);

  // ✅ Fetch products on filter or page change
  useEffect(() => {
    dispatch(
      fetchProducts({
        page: currentPage,
        filters: memoFilters,
      })
    );
  }, [dispatch, currentPage, memoFilters]);

  // ✅ Notification handler
  useEffect(() => {
    if (error && error !== null) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "حدث خطأ غير متوقع";

      toast.error(errorMessage, { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }

    if (success) {
      const modalElement = document.getElementById("deleteProductModal");
      let modalInstance = bootstrap.Modal.getInstance(modalElement);
      if (!modalInstance) modalInstance = new bootstrap.Modal(modalElement);
      modalInstance.hide();

      setDeletingProduct(null);

      setTimeout(() => {
        toast.success("تم الحذف بنجاح", {
          position: "top-center",
          zIndex: 9999,
          autoClose: 2000,
        });
      }, 200);

      dispatch(
        fetchProducts({
          page: currentPage,
          filters: memoFilters,
        })
      );

      dispatch(clearState());
    }

  }, [error, success, dispatch, currentPage, memoFilters]);

  useEffect(() => {
    const btn = document.getElementById("confirmDeleteBtn");
    if (!btn) return;
    const handleClick = () => {
      if (deletingProduct) {
        dispatch(deleteProduct(deletingProduct.id));
      }
    };
    btn.addEventListener("click", handleClick);
    return () => btn.removeEventListener("click", handleClick);
  }, [deletingProduct, dispatch]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const filters = Object.fromEntries(
      Object.entries(searchInputs).filter(
        ([_, value]) => value !== "" && value !== null
      )
    );

    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const emptyFilters = {
      category_id: "",
      grade_id: "",
      subject_id: "",
      section_id: "",
      teacher_id: "",
      status: "",
      min_price: "",
      max_price: "",
      has_discount: "",
      min_discount: "",
      max_discount: "",
      stock_status: "",
      date_from: "",
      date_to: "",
      sort_by: "",
      sort_dir: "",
    };

    setSearchInputs(emptyFilters);
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleEdit = (product) => {
    navigate(`/products/edit/${product.id}`);
  };

  const handlePageChange = (page) => setCurrentPage(page);


  const renderPagination = () => {
    if (!meta?.last_page || meta.last_page <= 1) return null;

    const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
    const pages = [];
    for (let i = s; i <= e; i++) {
      pages.push(
        <button key={i} onClick={() => i !== currentPage && handlePageChange(i)} type="button"
          style={{ width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
            background: i === currentPage ? "var(--red-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer" }}>
          {i}
        </button>
      );
    }
    const nb = (dis, lbl, fn) => (
      <button onClick={fn} disabled={dis} type="button"
        style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: dis ? "#cbd5e1" : "#475569", cursor: dis ? "not-allowed" : "pointer", fontWeight: 500 }}>
        {lbl}
      </button>
    );
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
        {nb(currentPage === 1, "السابق", () => handlePageChange(currentPage - 1))}
        <div style={{ display: "flex", gap: 4 }}>{pages}</div>
        {nb(currentPage === meta.last_page, "التالي", () => handlePageChange(currentPage + 1))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <div className="spinner-border text-danger" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .prod-custom-select {
          width: 100% !important;
          padding: 9px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .prod-custom-select:focus {
          border-color: var(--red-color) !important;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
        .prod-custom-date {
          width: 100% !important;
          padding: 9px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          color: #0f172a !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .prod-custom-date:focus {
          border-color: var(--red-color) !important;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
        .prod-custom-date.empty {
          color: #94a3b8 !important;
        }
        .prod-input {
          width: 100% !important;
          padding: 9px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .prod-input:focus {
          border-color: var(--red-color) !important;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
        .filter-label {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          display: block;
          margin-bottom: 6px;
          text-align: right;
        }
        .prod-table {
          width: 100%;
          border-collapse: collapse;
          text-align: center;
        }
        .prod-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .prod-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20 }}>📚 إدارة المنتجات التعليمية</h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تصفح وتحديث جميع الكتب والمذكرات والأكواد التعليمية.</p>
        </div>
        <PermissionButton
          className="btn text-white"
          onClick={() => navigate("/products/add")}
          permission="create-products"
          style={{ background: "linear-gradient(135deg,var(--red-color),#dc2626)", fontWeight: 700, borderRadius: 10, padding: "9px 20px", border: "none" }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          إضافة منتج جديد
        </PermissionButton>
      </div>

      {/* Filters */}
      <form onSubmit={handleFilterSubmit}
        style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, alignItems: "flex-end" }}>
          <div>
            <label className="filter-label">الفئة</label>
            <select name="category_id" value={searchInputs.category_id} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              {categories?.filter(cat => isBookOrCodeCategory(cat.name_en)).map((cat) => (
                <option key={cat?.id} value={cat?.id}>
                  {i18n.language === "ar" ? cat?.name_ar : cat?.name_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="filter-label">الصفوف</label>
            <select name="grade_id" value={searchInputs.grade_id} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              {grades?.map((grade) => (
                <option key={grade?.id} value={grade?.id}>
                  {i18n.language === "ar" ? grade?.name_ar : grade?.name_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="filter-label">المواد</label>
            <select name="subject_id" value={searchInputs.subject_id} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              {subjects?.map((subject) => (
                <option key={subject?.id} value={subject?.id}>
                  {i18n.language === "ar" ? subject?.name_ar : subject?.name_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="filter-label">الأقسام</label>
            <select name="section_id" value={searchInputs.section_id} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              {/* BUG-47 fix: إزالة التكرار من قائمة الأقسام قبل عرضها */}
              {Array.from(new Map((sections || []).map((section) => [section?.id, section])).values()).map((section) => (
                <option key={section?.id} value={section?.id}>
                  {i18n.language === "ar" ? section?.name_ar : section?.name_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="filter-label">المعلمين</label>
            <select name="teacher_id" value={searchInputs.teacher_id} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              {teachers?.map((teacher) => (
                <option key={teacher?.id} value={teacher?.id}>
                  {teacher?.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="filter-label">الحالة</label>
            <select name="status" value={searchInputs.status} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              <option value="1">🟢 نشط</option>
              <option value="0">🔴 غير نشط</option>
            </select>
          </div>

          <div>
            <label className="filter-label">الحد الأدنى للسعر</label>
            <input className="prod-input" name="min_price" type="number" value={searchInputs.min_price} onChange={handleSearchChange} placeholder="0.00" />
          </div>

          <div>
            <label className="filter-label">الحد الأقصى للسعر</label>
            <input className="prod-input" name="max_price" type="number" value={searchInputs.max_price} onChange={handleSearchChange} placeholder="0.00" />
          </div>

          <div>
            <label className="filter-label">يحتوي خصم؟</label>
            <select name="has_discount" value={searchInputs.has_discount} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              <option value="1">نعم</option>
              <option value="0">لا</option>
            </select>
          </div>

          <div>
            <label className="filter-label">الحد الأدنى للخصم</label>
            <input className="prod-input" name="min_discount" value={searchInputs.min_discount} onChange={handleSearchChange} type="number" placeholder="0%" />
          </div>

          <div>
            <label className="filter-label">الحد الأقصى للخصم</label>
            <input className="prod-input" name="max_discount" value={searchInputs.max_discount} onChange={handleSearchChange} type="number" placeholder="100%" />
          </div>

          <div>
            <label className="filter-label">حالة المخزون</label>
            <select name="stock_status" value={searchInputs.stock_status} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الكل</option>
              <option value="in">متوفر</option>
              <option value="out">غير متوفر</option>
              <option value="low">قليل</option>
            </select>
          </div>

          <div>
            <label className="filter-label">فرز حسب</label>
            <select name="sort_by" value={searchInputs.sort_by} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الافتراضي</option>
              <option value="newest">الأحدث</option>
              <option value="oldest">الأقدم</option>
              <option value="price">السعر</option>
              <option value="title">العنوان</option>
              <option value="stock">المخزون</option>
            </select>
          </div>

          <div>
            <label className="filter-label">اتجاه الترتيب</label>
            <select name="sort_dir" value={searchInputs.sort_dir} onChange={handleSearchChange} className="prod-custom-select">
              <option value="">الافتراضي</option>
              <option value="asc">تصاعدي</option>
              <option value="desc">تنازلي</option>
            </select>
          </div>

          <div>
            <label className="filter-label">التاريخ من</label>
            <input
              type={fromFocused || searchInputs.date_from ? "date" : "text"}
              name="date_from"
              placeholder="اختر تاريخ البدء"
              value={searchInputs.date_from}
              onChange={handleSearchChange}
              onFocus={() => setFromFocused(true)}
              onBlur={() => setFromFocused(false)}
              className={`prod-custom-date${!searchInputs.date_from ? " empty" : ""}`}
            />
          </div>

          <div>
            <label className="filter-label">التاريخ إلى</label>
            <input
              type={toFocused || searchInputs.date_to ? "date" : "text"}
              name="date_to"
              placeholder="اختر تاريخ الانتهاء"
              value={searchInputs.date_to}
              onChange={handleSearchChange}
              onFocus={() => setToFocused(true)}
              onBlur={() => setToFocused(false)}
              className={`prod-custom-date${!searchInputs.date_to ? " empty" : ""}`}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
          <button type="submit"
            style={{ padding: "10px 30px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            عرض النتائج
          </button>
          <button
            type="button"
            style={{ padding: "10px 24px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 600, cursor: "pointer" }}
            onClick={handleReset}
          >
            إعادة ضبط الكل
          </button>
        </div>
      </form>

      {/* Products Table */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <div className="table-responsive">
          <table className="prod-table">
            <thead>
              <tr>
                <th>#</th>
                <th>الصور</th>
                <th>العنوان</th>
                <th>الأسعار</th>
                <th>المخزون</th>
                <th>SKU</th>
                <th>الحالة</th>
                <th>التصنيف</th>
                <th>الصف</th>
                <th>المادة</th>
                <th>الشعبة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {filteredProducts?.map((product, index) => (
                <tr key={product.id}>
                  <td style={{ color: "#64748b" }}>
                    {(currentPage - 1) * (meta?.per_page ?? 10) + index + 1}
                  </td>

                  <td>
                    <div className="d-flex gap-1 justify-content-center">
                      {(product.sliders || [])
                        .slice(0, 3)
                        .map((slider, i) => (
                          <img
                            key={i}
                            src={slider.image}
                            alt={`product-${i}`}
                            style={{
                              width: "44px",
                              height: "44px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0"
                            }}
                          />
                        ))}
                    </div>
                  </td>

                  <td>
                    <div style={{ textAlign: "right" }}>
                      <strong style={{ fontSize: 14, color: "#0f172a" }}>{product.title_ar}</strong>
                      {product.title_en && (
                        <div style={{ fontSize: 12, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>
                          {product.title_en}
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div>
                      <span className="text-decoration-line-through text-muted" style={{ fontSize: 12 }}>
                        {product.fake_price} ج.م
                      </span>
                      <br />
                      <strong className="text-success" style={{ fontSize: 15 }}>
                        {product.real_price} ج.م
                      </strong>
                      {/* BUG-07 fix: حساب نسبة الخصم الفعلية من السعر القديم والجديد بدلاً من الاعتماد على حقل discount غير الدقيق */}
                      {(() => {
                        const oldPrice = parseFloat(product.fake_price) || 0;
                        const newPrice = parseFloat(product.real_price) || 0;
                        const discountPercent = oldPrice > 0 && newPrice < oldPrice
                          ? Math.round((1 - newPrice / oldPrice) * 100)
                          : 0;
                        return discountPercent > 0 && (
                          <span className="badge bg-danger ms-1" style={{ fontSize: 10 }}>
                            {discountPercent}%
                          </span>
                        );
                      })()}
                    </div>
                  </td>

                  <td>
                    <div style={{ fontSize: 13, color: "#475569" }}>
                      <span>الكمية: <strong>{product.qty}</strong></span>
                      <br />
                      <span>المخزون: <strong>{product.stock}</strong></span>
                    </div>
                  </td>
                  <td>
                    <code style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, color: "#475569", fontSize: 12 }}>
                      {product.sku || "—"}
                    </code>
                  </td>

                  <td>
                    <span
                      className={`badge ${product.status === "1" || product.status === 1 || product.status === "active"
                        ? "bg-success text-white"
                        : "bg-secondary text-white"
                        }`}
                      style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}
                    >
                      {product.status === "1" || product.status === 1 || product.status === "active"
                        ? "نشط"
                        : "غير نشط"}
                    </span>
                  </td>

                  <td>
                    <div>
                      <strong style={{ fontSize: 13, color: "#1e40af" }}>{product.category_name_ar ? product.category_name_ar : "غير محدد"}</strong>
                      <br />
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        {product.category_name_en ? product.category_name_en : "—"}
                      </small>
                    </div>
                  </td>

                  <td>
                    <div>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{product.grade_name_ar ? product.grade_name_ar : "غير محدد"}</span>
                      {product.grade_name_en && (
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{product.grade_name_en}</div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{product.subject_name_ar ? product.subject_name_ar : "غير محدد"}</span>
                      {product.subject_name_en && (
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{product.subject_name_en}</div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{product.section_name_ar ? product.section_name_ar : "غير محدد"}</span>
                      {product.section_name_en && (
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{product.section_name_en}</div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className="d-flex justify-content-center gap-1">
                      <PermissionButton
                        className="btn btn-warning btn-sm"
                        onClick={() => handleEdit(product)}
                        permission="edit-products"
                        title="تعديل"
                        style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <FaEdit size={14} />
                      </PermissionButton>

                      <PermissionButton
                        className="btn btn-danger btn-sm"
                        permission="delete-products"
                        title="حذف"
                        onClick={() => {
                          setDeletingProduct(product);
                          const deleteModal = new bootstrap.Modal(
                            document.getElementById("deleteProductModal")
                          );
                          deleteModal.show();
                        }}
                        style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <FaTrash size={14} />
                      </PermissionButton>
                    </div>
                  </td>
                </tr>
              ))}

              {(!filteredProducts || filteredProducts.length === 0) && (
                <tr>
                  <td colSpan="12" className="text-center py-4" style={{ color: "#94a3b8" }}>
                    لا توجد نتائج
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* Delete Confirmation Modal */}
      <div
        className="modal fade"
        id="deleteProductModal"
        tabIndex="-1"
        aria-labelledby="deleteProductModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: 16, overflow: "hidden", border: "none", boxShadow: "0 25px 60px rgba(0,0,0,.2)" }}>
            <div className="modal-header d-flex justify-content-between" style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
              <h5 className="modal-title d-flex align-items-center gap-2" id="deleteProductModalLabel" style={{ fontWeight: 700, fontSize: 16 }}>
                <FaTrash /> تأكيد الحذف
              </h5>
              <button
                type="button"
                className="btn-close mx-0"
                data-bs-dismiss="modal"
                aria-label="Close"
                style={{ padding: 0, margin: 0 }}
              ></button>
            </div>
            <div className="modal-body" style={{ padding: "20px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف المنتج؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                {deletingProduct?.title_ar}
              </div>
              <p className="text-danger" style={{ margin: "10px 0 0", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div className="modal-footer" style={{ padding: "14px 24px", background: "#f8fafc", borderTop: "none" }}>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600 }}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn btn-danger"
                id="confirmDeleteBtn"
                disabled={deleteLoading}
                style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600 }}
              >
                {deleteLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    جاري الحذف...
                  </>
                ) : (
                  "حذف المنتج"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
